// @ts-nocheck

import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import { connect } from 'react-redux';
import { getSelectedProduct } from '../../../../main/screens/ScreenNewSummary/selectors/pageStateSelectors';

import { modifyQueryForFraud } from '../../../../main/screens/ScreenAdsFraud/fraudUtils';
import { scaledDebounce } from '../../../../main/support/scaledDebounce';
import { getDruidData } from '../../../actions/DruidActions';
import { getQueries, getReportTotals } from '../../../selectors/DruidSelectors';
import { DruidQueryHashService } from '../../../services/druid/DruidQueryHashService';
import { alterQuery, getQueryWithPreviousPeriod, parseCalculatedDatasources } from '../../../support/utils';
import { cleanEmptyFilters } from '../../../support/utils/druid/updateQuery';

const MAX_POLL_DURATION = 1000 * 60 * 5; // 5 minutes
const MIN_INTERVAL = 3;

const NOT_FOUND_QUERY = {
  loading: false,
  error: null,
  data: null,
};

const INIT_DEBOUNCE = 500;
const BASE_DEBOUNCE = 1000;
const DEBOUNCE_MULTIPLIER = 250;
const MAX_DEBOUNCE = 2000;

class DruidQuery extends React.Component {
  static propTypes = {
    /**
     * Component that will get all the Druid request props calculated by the DruidQuery
     */
    children: PropTypes.any,
    /**
     * value should be the curried result of dispatch from DruidActions.getDruidData
     */
    getDruidData: PropTypes.func.isRequired,
    /**
     * specify query type, these translate to seperate endpoints to druid query service
     * NOTE: 'timeseries' is not used on the dashboard; it only works when no
     * dimensions are needed, which is not the case anywhere on the dashboard currently.
     */
    type: PropTypes.oneOf(['groupby', 'topn', 'timeseries', 'cohorts']).isRequired,
    /**
     * initialized values for the query, the object will be merged with BASE_QUERY
     */
    query: PropTypes.shape({
      app_id: PropTypes.string,
      start_date: PropTypes.string.isRequired,
      end_date: PropTypes.string.isRequired,
      data_sources: PropTypes.array.isRequired,
      context: PropTypes.string.isRequired,
      dimensions: PropTypes.array,
      filters: PropTypes.objectOf(PropTypes.array),
      debug: PropTypes.bool,
      eo_cutoff_date: PropTypes.string,
      granularity: PropTypes.string,
      // any additional queries to be dispatched separately from the main query
      additionalQueries: PropTypes.arrayOf(PropTypes.object),
    }),
    /**
     * source specific options for the query
     * example:
     *  {
     *    eo_commerce_event: {
     *      filters: { name: [ 'purchase' ] }
     *    }
     * }
     */
    sourceQuery: PropTypes.object,
    /**
     * Poll time in seconds
     */
    pollInterval: PropTypes.number,
    /**
     * overrides for v1/druid.js logic
     */
    overrides: PropTypes.shape({
      /**
       * To retrive all events, not just referred events
       * see: server/v1/druidUtils.js:@addMandatoryFilters
       */
      disableReferredOnly: PropTypes.bool,
    }),
    /**
     * Retrive unique counts based on personas
     * when unique == true, the following fields are appended / merged with query (see @alterQuery)
     * {
     *   "metric": "unique_count",
     *   "aggregations": [..., "unique_count" ]
     * }
     *
     */
    unique: PropTypes.bool,
    /**
     * Retrieve organic counts based "attributed" column
     * when organic == true, we wrap the query filter in one OR filter with attributed=false
     *
     * "filter": {
     *   "type": "or",
     *   "fields": [
     *     {
     *       "type": "and",
     *       "fields": [
     *         {
     *           "type": "in",
     *           "dimension": "attributed",
     *           "values": [
     *           false
     *        ]
     *         }
     *       }
     *     },
     *     {
     *       "type": "and",
     *       "fields": [
     *         {
     *           "type": "in",
     *           "dimension": "last_attributed_touch_data_tilde_feature",
     *           "values": [
     *             "paid advertising"
     *           ]
     *         }
     *       }
     *     }
     *   ]
     * }
     *
     */
    organic: PropTypes.bool,
    webOnly: PropTypes.bool,
    includePreviousPeriod: PropTypes.bool,

    debounce: PropTypes.bool,

    /* for styling wrapper in case it affects the children */
    className: PropTypes.string,

    reportQuery: PropTypes.any,

    trendsQuery: PropTypes.any,

    /**
     * Optional. Default: flat. If set to tree, when using tree view mode on report table
     */
    reportActiveMode: PropTypes.string,
  };
  static defaultProps = {
    getDruidData: () => {},
    query: {},
    queries: {},
    reportActiveMode: 'flat',
  };

  constructor(props) {
    super(props);

    this.hashService = new DruidQueryHashService();

    this.state = { scheduled: false };

    this._queryRunner = this._runQuery;
    if (props.debounce) {
      this._queryRunner = scaledDebounce(this._runQuery, BASE_DEBOUNCE, {
        multiplier: DEBOUNCE_MULTIPLIER,
        max: MAX_DEBOUNCE,
      });
    }

    if (props.hasOwnProperty('pollInterval')) {
      if (props.pollInterval < MIN_INTERVAL) {
        throw 'Poll interval must be great than 5s';
      }

      this._pollLimit = Math.floor(MAX_POLL_DURATION / (props.pollInterval * 1000));
      this._pollCount = 0;

      this._poll = setInterval(this._pollGetData, props.pollInterval * 1000);
    }
  }

  componentWillMount() {
    this.setState({ scheduled: true });

    if (!this.props.debounce) {
      return this._runQuery(this.props);
    }

    this._initTimer = setTimeout(() => this._runQuery(this.props), INIT_DEBOUNCE);
  }

  componentWillUnmount() {
    clearInterval(this._poll);
    clearTimeout(this._initTimer);
  }

  componentWillReceiveProps(nextProps) {
    const props = this.props;
    const propList = [
      'query',
      'sourceQuery',
      'type',
      'overrides',
      'unique',
      'organic',
      'webOnly',
      'additionalQueries',
      'reportQuery',
      'trendsQuery',
      'reportActiveMode',
    ];
    const nextPropsAreEqual = R.equals(R.pick(propList, props), R.pick(propList, nextProps));

    if (nextPropsAreEqual) {
      return;
    }

    clearTimeout(this._initTimer);
    this.setState({ scheduled: true });
    this._queryRunner(nextProps);
  }

  _runQuery = (props) => {
    this.setState({ scheduled: false });
    const { sourceQuery, type, overrides, organic, includePreviousPeriod, additionalQueries } = props;

    const query = this._createQuery(props);

    let reportQuery = props.reportQuery;
    if (reportQuery) {
      reportQuery = {
        filter: query.filter,
        ...reportQuery,
      };
    }

    let trendsQuery = props.trendsQuery;
    if (trendsQuery) {
      trendsQuery = {
        filter: query.filter,
        ...trendsQuery,
      };
    }

    this.props.getDruidData(type, {
      query,
      sourceQuery,
      overrides,
      organic,
      reportQuery,
      trendsQuery,
    });

    if (includePreviousPeriod) {
      this.props.getDruidData(type, {
        query: getQueryWithPreviousPeriod(query),
        sourceQuery,
        overrides,
        organic,
      });
    }

    if (additionalQueries && Object.keys(additionalQueries).length > 0) {
      Object.keys(additionalQueries).forEach((baseline) => {
        const query = additionalQueries[baseline];
        this.props.getDruidData(type, {
          query,
          sourceQuery,
          overrides,
          organic,
        });
      });
    }
  };

  _pollGetData = () => {
    const { sourceQuery, getDruidData, type, overrides, organic } = this.props;
    getDruidData(type, {
      query: this._createQuery(this.props),
      sourceQuery,
      overrides,
      organic,
    });

    ++this._pollCount;

    if (this._pollCount > this._pollLimit) {
      clearInterval(this._poll);
    }
  };

  _createQuery = ({ query, unique, webOnly }) => {
    let updatedQuery = alterQuery(query, unique, webOnly);
    updatedQuery = parseCalculatedDatasources(updatedQuery);
    updatedQuery = this._cleanEmptyFilters(updatedQuery);
    // [EG-2380] [EG-3109] see modifyQueryForFraud definition for more info
    updatedQuery = modifyQueryForFraud(updatedQuery);

    return updatedQuery;
  };

  _cleanEmptyFilters = (query) => {
    let updatedQuery = query;
    if (updatedQuery.filter) {
      updatedQuery = {
        ...updatedQuery,
        filter: cleanEmptyFilters(updatedQuery.filter, true),
      };
    }
    return updatedQuery;
  };

  render() {
    const { queries, query, sourceQuery, overrides, unique, webOnly, organic, includePreviousPeriod, additionalQueries } =
      this.props;

    const updatedQuery = this._createQuery(this.props);

    let reportQuery = this.props.reportQuery;
    if (reportQuery) {
      reportQuery = {
        filter: updatedQuery.filter,
        metric: updatedQuery.metric,
        organic,
        ...reportQuery,
      };
    }

    const hash = this.hashService.getHash({
      ...this.props,
      query: updatedQuery,
    });

    const queryResults = queries[hash] || NOT_FOUND_QUERY;
    const { limitReached, limit, isReportData } = queryResults;

    const reportTotals = this.props.reportTotals[hash];

    // loading and error will take into account the original query and prevQuery if available
    let error = queryResults.error;
    let loading = queryResults.loading || this.state.scheduled;

    const data = !loading ? queryResults.data : undefined;
    const isPartialLoad = this.props.isPartialLoad && data && !limitReached;

    const limitedResults = !loading && data?.length && queryResults.limitedResults;
    const movedPlatformValues = !loading && data?.length && queryResults.movedPlatformValues;
    const explanation = queryResults.explanation;

    let prevHash;
    let prevData;
    let additionalHashes;
    let additionalData;
    let prevDateRange;
    if (includePreviousPeriod) {
      const tmpQuery = getQueryWithPreviousPeriod(updatedQuery);

      prevHash = this.hashService.getHash({
        ...this.props,
        query: tmpQuery,
      });
      const prevQuery = queries[prevHash] || NOT_FOUND_QUERY;

      if (prevQuery) {
        prevData = prevQuery.data;
        loading = loading || prevQuery.loading;
        error = error || prevQuery.error;
        prevDateRange = {
          start_date: tmpQuery?.start_date,
          end_date: tmpQuery?.end_date,
        };
      }
    }

    if (additionalQueries && Object.keys(additionalQueries).length > 0) {
      additionalHashes = Object.keys(additionalQueries).reduce((acc, b) => {
        const hash = this.hashService.getHash({
          ...this.props,
          query: alterQuery(additionalQueries[b], unique, webOnly),
        });
        return R.merge(acc, { [b]: hash });
      }, {});
      const results = R.map((h) => queries[h] || NOT_FOUND_QUERY, additionalHashes);

      additionalData = R.map((r) => {
        loading = loading || r.loading;
        error = error || r.error;
        return r.data;
      }, results);
    }

    const dateRange = {
      start_date: query.start_date,
      end_date: query.end_date,
    };

    return (
      <div className={this.props.className}>
        {React.Children.map(this.props.children, (child) => {
          if (child) {
            return React.cloneElement(child, {
              loading,
              error,
              data,
              hash,
              prevHash,
              prevData,
              additionalHashes,
              additionalData,
              additionalQueries,
              query,
              unique,
              organic,
              sourceQuery,
              overrides,
              limitReached,
              limit,
              isPartialLoad,
              isReportData,
              reportTotals,
              limitedResults,
              movedPlatformValues,
              reportQuery,
              explanation,
              prevDateRange,
              dateRange,
            });
          }
        })}
      </div>
    );
  }
}

const mapDispatchProps = (dispatch, ownProps) => ({
  getDruidData: (type, query) => {
    return dispatch(getDruidData(type, query, ownProps.reportActiveMode));
  },
});

const mapStateProps = (state) => {
  return {
    queries: getQueries(state),
    reportTotals: getReportTotals(state),
    summarySelectedProduct: getSelectedProduct(state),
  };
};

export default connect(mapStateProps, mapDispatchProps)(DruidQuery);
